<template>
  <form class="vm-vue-filter vm-list-filter-panel" @submit.prevent="submitFilter">
    <div class="vm-list-filter-panel-header">
      <div class="vm-list-filter-panel-title">ISO Image Filters</div>
      <div v-if="loading" class="vm-list-vue-filter-header-status">Searching...</div>
    </div>

    <div class="vm-vue-filter-grid vm-list-vue-filter-grid">
      <div class="vm-vue-filter-field vm-list-vue-filter-field">
        <span class="vm-vue-filter-label">Name</span>
        <input v-model="name" type="text" class="form-control" placeholder="Filter ISO images by name">
      </div>

      <div v-if="showUserFilter" class="vm-vue-filter-field vm-list-vue-filter-field">
        <span class="vm-vue-filter-label">User</span>
        <div ref="userPickerRef" class="vm-search-select vm-list-vue-filter-picker">
          <button type="button" class="form-control vm-search-select-trigger" :aria-expanded="userPickerOpen ? 'true' : 'false'" @click.stop="toggleUserPicker">
            <span class="vm-search-select-trigger-label">{{ userTriggerLabel }}</span>
            <span>{{ userPickerOpen ? '▴' : '▾' }}</span>
          </button>
          <div v-if="userPickerOpen" class="vm-search-select-panel" @click.stop>
            <input v-model="userFilter" type="text" class="form-control vm-search-select-input" placeholder="Filter users">
            <div class="vm-search-select-options">
              <button v-for="option in filteredUserOptions" :key="`user-${option.value}`" type="button" class="vm-search-select-option" @click="selectUser(option.value)">
                {{ option.label }}
              </button>
              <div v-if="!filteredUserOptions.length" class="vm-list-vue-filter-empty">No matching users</div>
            </div>
          </div>
        </div>
      </div>

      <div class="vm-vue-filter-field vm-list-vue-filter-field">
        <span class="vm-vue-filter-label">OS</span>
        <div ref="osPickerRef" class="vm-search-select vm-list-vue-filter-picker">
          <button type="button" class="form-control vm-search-select-trigger" :aria-expanded="osPickerOpen ? 'true' : 'false'" @click.stop="toggleOsPicker">
            <span class="vm-search-select-trigger-label">{{ osTriggerLabel }}</span>
            <span>{{ osPickerOpen ? '▴' : '▾' }}</span>
          </button>
          <div v-if="osPickerOpen" class="vm-search-select-panel" @click.stop>
            <input v-model="osFilter" type="text" class="form-control vm-search-select-input" placeholder="Filter OS types">
            <div class="vm-search-select-options">
              <button v-for="option in filteredOsOptions" :key="`os-${option.value}`" type="button" class="vm-search-select-option" @click="addOs(option.value)">
                {{ option.label }}
              </button>
              <div v-if="!filteredOsOptions.length" class="vm-list-vue-filter-empty">No matching OS types</div>
            </div>
          </div>
        </div>
      </div>

      <div class="vm-vue-filter-actions">
        <button type="button" class="btn btn-default vm-list-vue-filter-reset" :disabled="loading" @click="resetFilter">
          Reset Filters
        </button>
      </div>
    </div>

    <div v-if="selectedOsDetails.length" class="vm-list-vue-filter-selected-wrap">
      <div class="vm-list-vue-filter-selected-label">Selected OS</div>
      <div class="vm-list-vue-filter-selected">
        <button v-for="option in selectedOsDetails" :key="`selected-os-${option.value}`" type="button" class="vm-list-vue-selected-chip vm-list-vue-selected-chip-os" @click="removeOs(option.value)">
          <span>{{ option.label }}</span>
          <span class="vm-list-vue-selected-chip-x">x</span>
        </button>
      </div>
    </div>
  </form>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  endpoint: { type: String, required: true },
  initialName: { type: String, default: '' },
  initialUser: { type: String, default: '' },
  initialOs: { type: Array, default: () => [] },
  userOptions: { type: Array, default: () => [] },
  osOptions: { type: Array, default: () => [] },
  resultsTarget: { type: String, required: true }
})

const name = ref(props.initialName || '')
const selectedUser = ref(props.initialUser || '')
const selectedOs = ref([...(props.initialOs || [])])
const loading = ref(false)
const userPickerOpen = ref(false)
const osPickerOpen = ref(false)
const userFilter = ref('')
const osFilter = ref('')
const userPickerRef = ref(null)
const osPickerRef = ref(null)
let requestToken = 0
let debounceTimer = null

const showUserFilter = computed(() => props.userOptions.length > 0)
const normalizedUserOptions = computed(() => props.userOptions || [])
const normalizedOsOptions = computed(() => props.osOptions.filter((option) => option.value))
const selectedUserDetail = computed(() => normalizedUserOptions.value.find((option) => option.value === selectedUser.value))
const filteredUserOptions = computed(() => {
  const keyword = userFilter.value.trim().toLowerCase()
  if (!keyword) return normalizedUserOptions.value
  return normalizedUserOptions.value.filter((option) => option.label.toLowerCase().includes(keyword))
})
const availableOsOptions = computed(() => normalizedOsOptions.value.filter((option) => !selectedOs.value.includes(option.value)))
const selectedOsDetails = computed(() => normalizedOsOptions.value.filter((option) => selectedOs.value.includes(option.value)))
const filteredOsOptions = computed(() => {
  const keyword = osFilter.value.trim().toLowerCase()
  if (!keyword) return availableOsOptions.value
  return availableOsOptions.value.filter((option) => option.label.toLowerCase().includes(keyword))
})

const userTriggerLabel = computed(() => selectedUserDetail.value?.label || 'Select user')
const osTriggerLabel = computed(() => {
  if (!selectedOs.value.length) return 'Select OS types'
  if (selectedOs.value.length === 1) return selectedOsDetails.value[0]?.label || 'Select OS types'
  return `${selectedOs.value.length} OS types selected`
})

function buildUrl() {
  const url = new URL(props.endpoint, window.location.origin)
  if (name.value.trim()) url.searchParams.set('search[name]', name.value.trim())
  if (selectedUser.value) url.searchParams.set('search[user]', selectedUser.value)
  selectedOs.value.forEach((value) => {
    if (value) url.searchParams.append('search[os][]', value)
  })
  return url
}

async function updateResults() {
  const url = buildUrl()
  const currentToken = ++requestToken
  const response = await fetch(url.toString(), {
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin'
  })
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`)
  if (currentToken !== requestToken) return
  const data = await response.json()
  const target = document.querySelector(props.resultsTarget)
  if (target && target.dataset.vueComponent === 'iso-image-results' && data.results_data) {
    document.dispatchEvent(new CustomEvent('scc:iso-image-results:update', { detail: data.results_data }))
  }
  window.history.replaceState({}, '', `${window.location.pathname}${url.search}`)
}

async function submitFilter() {
  loading.value = true
  const startedAt = Date.now()
  document.dispatchEvent(new CustomEvent('scc:lazy-results:loading', {
    detail: {
      eventName: 'scc:iso-image-results:update',
      loading: true
    }
  }))
  try {
    await updateResults()
  } catch (error) {
    console.error('Failed to filter ISO images', error)
    window.alert('Failed to update ISO images.')
  } finally {
    const elapsed = Date.now() - startedAt
    if (elapsed < 250) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 - elapsed))
    }
    loading.value = false
    document.dispatchEvent(new CustomEvent('scc:lazy-results:loading', {
      detail: {
        eventName: 'scc:iso-image-results:update',
        loading: false
      }
    }))
  }
}

function scheduleSubmit() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = window.setTimeout(() => submitFilter(), 300)
}

function selectUser(value) {
  selectedUser.value = value || ''
  userFilter.value = ''
  userPickerOpen.value = false
  submitFilter()
}

function addOs(value) {
  if (!value) return
  if (!selectedOs.value.includes(value)) selectedOs.value = [...selectedOs.value, value]
  osFilter.value = ''
  osPickerOpen.value = false
  submitFilter()
}

function removeOs(value) {
  selectedOs.value = selectedOs.value.filter((entry) => entry !== value)
  submitFilter()
}

function resetFilter() {
  name.value = ''
  selectedUser.value = ''
  userFilter.value = ''
  selectedOs.value = []
  osFilter.value = ''
  userPickerOpen.value = false
  osPickerOpen.value = false
  submitFilter()
}

function toggleUserPicker() {
  userPickerOpen.value = !userPickerOpen.value
  osPickerOpen.value = false
  if (!userPickerOpen.value) userFilter.value = ''
}

function toggleOsPicker() {
  osPickerOpen.value = !osPickerOpen.value
  userPickerOpen.value = false
  if (!osPickerOpen.value) osFilter.value = ''
}

function closePickersOnOutsideClick(event) {
  if (userPickerRef.value && !userPickerRef.value.contains(event.target)) {
    userPickerOpen.value = false
    userFilter.value = ''
  }
  if (osPickerRef.value && !osPickerRef.value.contains(event.target)) {
    osPickerOpen.value = false
    osFilter.value = ''
  }
}

watch(name, () => {
  scheduleSubmit()
})

onMounted(() => {
  document.addEventListener('click', closePickersOnOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closePickersOnOutsideClick)
  if (debounceTimer) clearTimeout(debounceTimer)
})
</script>

<style scoped>
.vm-vue-filter {
  margin-bottom: 18px;
}

.vm-list-filter-panel {
  padding: 18px 20px;
  border: 1px solid #d9e2ec;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(241, 245, 249, 0.96));
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.vm-list-filter-panel-header {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vm-list-filter-panel-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
}

.vm-list-vue-filter-header-status {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.vm-vue-filter-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
}

.vm-vue-filter-field {
  min-width: 220px;
  flex: 1 1 240px;
  margin: 0;
}

.vm-list-vue-filter-field {
  flex: 1 1 240px;
}

.vm-vue-filter-label {
  display: block;
  margin-bottom: 6px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.vm-list-vue-filter-picker {
  width: 100%;
  max-width: 320px;
}

.vm-list-vue-filter-selected-wrap {
  margin-top: 14px;
}

.vm-list-vue-filter-selected-label {
  margin-bottom: 8px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.vm-list-vue-filter-selected {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.vm-list-vue-selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 7px 12px;
  border: 1px solid;
  border-radius: 999px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.vm-list-vue-selected-chip-os {
  color: #0f172a;
  background: #dbeafe;
  border-color: #60a5fa;
}

.vm-list-vue-selected-chip-x {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.vm-list-vue-filter-empty {
  padding: 10px 12px;
  color: #64748b;
  font-size: 12px;
}

.vm-vue-filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vm-list-vue-filter-reset {
  min-width: 120px;
  min-height: 42px;
  padding: 10px 16px;
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  font-weight: 600;
}

.vm-list-vue-filter-reset:hover,
.vm-list-vue-filter-reset:focus {
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #e2e8f0 100%);
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.16);
}

.vm-list-vue-filter-reset:disabled {
  color: #94a3b8;
  background: #f8fafc;
  border-color: #e2e8f0;
  box-shadow: none;
}
</style>
